import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { BankCandidateClientDto } from '@crm/shared'
import { getClients } from '../../shared/api/clients.api'

/**
 * Клиент платежа в окне разноса: найденный по ИНН, выбор из нескольких с одним ИНН
 * или поиск по названию, когда по ИНН никого нет.
 */
export function BankClientPicker({
  clients,
  clientId,
  onPick,
}: {
  clients: BankCandidateClientDto[]
  clientId: string | null
  onPick: (id: string | undefined) => void
}) {
  const [search, setSearch] = useState('')
  const [searching, setSearching] = useState(false)
  const term = search.trim()
  const found = useQuery({
    queryKey: ['bank-client-search', term],
    queryFn: () => getClients({ search: term, limit: 20 }),
    enabled: searching && term.length >= 2,
  })
  const current = clients.find((c) => c.id === clientId) ?? null

  if (current && !searching) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="text-gray-500">Клиент:</span>
        <span className="font-medium text-gray-900">{current.name}</span>
        <span className="text-gray-500">
          {current.inn ? `ИНН ${current.inn}` : 'ИНН не заполнен'}
          {current.managerName ? ` · менеджер ${current.managerName}` : ''}
        </span>
        <button
          type="button"
          onClick={() => setSearching(true)}
          className="text-xs text-brand-600 hover:text-brand-700"
        >
          Выбрать другого
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-2 text-sm">
      {clients.length > 1 && !searching && (
        <div>
          <div className="mb-1 text-gray-500">С этим ИНН несколько клиентов — чей это платёж?</div>
          <div className="flex flex-wrap gap-2">
            {clients.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onPick(c.id)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 hover:border-brand-500"
              >
                {c.name}
                {c.managerName ? ` · ${c.managerName}` : ''}
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <label className="mb-1 block text-gray-500" htmlFor="bank-client-search">
          Найти клиента по названию
        </label>
        <input
          id="bank-client-search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setSearching(true)
          }}
          placeholder="Начните вводить название клиента"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>
      {searching && term.length >= 2 && (
        <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200">
          {found.isLoading ? (
            <div className="px-3 py-2 text-gray-500">Ищу…</div>
          ) : (found.data?.items ?? []).length === 0 ? (
            <div className="px-3 py-2 text-gray-500">Клиентов с таким названием нет</div>
          ) : (
            (found.data?.items ?? []).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  onPick(c.id)
                  setSearching(false)
                  setSearch('')
                }}
                className="block w-full px-3 py-2 text-left hover:bg-gray-50"
              >
                <span className="font-medium text-gray-900">{c.name}</span>
                <span className="ml-2 text-gray-500">
                  {c.inn ? `ИНН ${c.inn}` : 'ИНН не заполнен'}
                </span>
              </button>
            ))
          )}
        </div>
      )}
      {searching && current && (
        <button
          type="button"
          onClick={() => setSearching(false)}
          className="text-xs text-gray-500 hover:text-gray-800"
        >
          Оставить «{current.name}»
        </button>
      )}
    </div>
  )
}
